import type { UploadItem } from '../hooks/useUploadQueue.js';
import { formatSize } from '../lib/format.js';
import './UploadPanel.css';

export function UploadPanel({
  items,
  onRetry,
  onCancel,
  onClear,
}: {
  items: UploadItem[];
  onRetry: (id: string) => void;
  onCancel: (id: string) => void;
  onClear: () => void;
}) {
  if (items.length === 0) return null;

  const finished = items.filter(
    (it) => it.status === 'completato' || it.status === 'errore' || it.status === 'annullato',
  ).length;

  return (
    <section className="uploads card" aria-label="Caricamenti">
      <header className="uploads__head">
        <h3>Caricamenti</h3>
        {finished > 0 ? (
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClear}>
            Pulisci completati
          </button>
        ) : null}
      </header>

      <ul className="uploads__list">
        {items.map((item) => (
          <li key={item.id} className={`uploads__item is-${item.status}`}>
            <div className="uploads__row">
              <span className="uploads__name" title={item.file.name}>
                {item.file.name}
                {/* La coda resta a schermo mentre si naviga: senza la
                    destinazione non si capisce dove finira' il file. */}
                <span className="uploads__dest" title={`Destinazione: ${item.dirName}`}>
                  → {item.dirName}
                </span>
              </span>
              <span className="uploads__size">{formatSize(item.file.size)}</span>
              <span className="uploads__status">{statusLabel(item)}</span>
              <span className="uploads__action">
                {item.status === 'errore' ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => onRetry(item.id)}
                  >
                    Riprova
                  </button>
                ) : item.status === 'in-corso' || item.status === 'in-coda' ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => onCancel(item.id)}
                  >
                    Annulla
                  </button>
                ) : null}
              </span>
            </div>

            {item.status === 'in-corso' || item.status === 'in-coda' ? (
              <div
                className="uploads__bar"
                role="progressbar"
                aria-valuenow={Math.round(item.progress * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className="uploads__bar-fill" style={{ width: `${item.progress * 100}%` }} />
              </div>
            ) : null}

            {item.error ? <p className="uploads__error">{item.error}</p> : null}

            {item.renamed && item.finalName ? (
              <p className="uploads__note">
                Un file con lo stesso nome esisteva già: salvato come{' '}
                <strong>{item.finalName}</strong>.
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function statusLabel(item: UploadItem): string {
  switch (item.status) {
    case 'in-coda':
      return 'In coda';
    case 'in-corso':
      return `${Math.round(item.progress * 100)}%`;
    case 'completato':
      return 'Completato';
    case 'errore':
      return 'Errore';
    case 'annullato':
      return 'Annullato';
  }
}
